import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubmissionWindow } from '../hooks/useSubmissionWindow';

const pad = (n: number) => String(n).padStart(2, '0');

/** Copy shown wherever ideas, votes or comments used to be collected once the window has closed. */
export const SubmissionClosedNotice: React.FC = () => {
  const { t } = useLanguage();
  return (
    <Paper
      role="status"
      elevation={0}
      sx={{
        p: 3,
        mb: 4,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'primary.light',
        bgcolor: 'rgba(107, 70, 193, 0.06)',
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      <InfoOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
      <Box>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
          {t.submissionWindow.closedTitle}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {t.submissionWindow.closedBody}
        </Typography>
      </Box>
    </Paper>
  );
};

/**
 * Counts down to the tenant's submission deadline and swaps itself for the closed
 * notice when it reaches zero. Renders nothing for tenants without a deadline.
 */
const SubmissionCountdown: React.FC = () => {
  const { t } = useLanguage();
  const { deadline, msRemaining, isClosed } = useSubmissionWindow({ tick: true });

  if (!deadline) return null;
  if (isClosed) return <SubmissionClosedNotice />;

  const totalSeconds = Math.floor(msRemaining / 1000);
  const units: { key: string; value: string; label: string }[] = [
    { key: 'days', value: String(Math.floor(totalSeconds / 86400)), label: t.submissionWindow.days },
    { key: 'hours', value: pad(Math.floor((totalSeconds % 86400) / 3600)), label: t.submissionWindow.hours },
    { key: 'minutes', value: pad(Math.floor((totalSeconds % 3600) / 60)), label: t.submissionWindow.minutes },
    { key: 'seconds', value: pad(totalSeconds % 60), label: t.submissionWindow.seconds },
  ];

  return (
    <Paper
      role="timer"
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 4,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'secondary.main',
        bgcolor: 'rgba(245, 158, 11, 0.08)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccessTimeIcon color="secondary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t.submissionWindow.countdownLabel}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 3 } }}>
        {units.map((unit) => (
          <Box key={unit.key} data-testid={`countdown-${unit.key}`} sx={{ textAlign: 'center', minWidth: 48 }}>
            <Typography
              variant="h5"
              component="span"
              sx={{ display: 'block', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
            >
              {unit.value}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {unit.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default SubmissionCountdown;
